import { FC } from "react"
import classNames from "classnames"
import { Icon, IconColor } from "../shared/Icon"
import Tooltip from "../shared/Tooltip"
import { WearableCardProps } from "."
import Button, { ButtonIconPosition } from "@/components/shared/Button"
import { PageRoutes } from "@/constants/routes"
import { useTruncation } from "@/hooks/useTruncation"
import { useUserRole } from "@/hooks/useUserRole"
import { ButtonVariant } from "@/types/index"
import { renderWithTooltip } from "@/utils/index"
import "./index.scss"

const WearableCardContent: FC<WearableCardProps> = ({
   inmateName,
   bookingNumber,
   location,
   bpm = "-",
   oxygenLevel = "-",
   awaitingVitals,
   lowBattery,
   className,
   hasAction,
   hideButtons = false,
   onSwapDevice,
   onViewDetails,
}) => {
   const [locationRef, isLocationTruncated] =
      useTruncation<HTMLSpanElement>(location)
   const [inmateNameRef, isInmateNameTruncated] =
      useTruncation<HTMLSpanElement>(inmateName)
   const [bookingNumberRef, isBookingNumberTruncated] =
      useTruncation<HTMLSpanElement>(bookingNumber)
   const userCanAccessDetailView = useUserRole(
      PageRoutes.INMATE_DETAIL_OVERVIEW
   )

   return (
      <div className={classNames("wearable-card__content", className)}>
         <div className="wearable-card__header">
            <div className="wearable-card__title">
               <Icon
                  className={classNames({
                     "wearable-card__battery-icon": lowBattery,
                  })}
                  size="16"
                  color={IconColor.WHITE}
                  name={lowBattery ? "batteryEmpty" : "smartwatch"}
               />
               {renderWithTooltip(
                  inmateNameRef,
                  "wearable-card__label",
                  inmateName,
                  isInmateNameTruncated
               )}
            </div>

            <div className="wearable-card__buttons">
               {hasAction && !hideButtons && (
                  <Button
                     className="wearable-card__button-swap"
                     iconName="moveInmate"
                     iconSize="8"
                     iconPosition={ButtonIconPosition.LEFT}
                     variant={ButtonVariant.SMALL}
                     onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        onSwapDevice?.()
                     }}
                  >
                     Swap device
                  </Button>
               )}
               {!hideButtons && userCanAccessDetailView && (
                  <Tooltip content="Go to detail overview">
                     <Button
                        variant={ButtonVariant.SMALL}
                        iconName="visual"
                        iconSize="12"
                        iconPosition={ButtonIconPosition.LEFT}
                        onClick={(e) => {
                           e.stopPropagation()
                           e.preventDefault()
                           onViewDetails?.()
                        }}
                     />
                  </Tooltip>
               )}
            </div>
         </div>
         {bookingNumber &&
            renderWithTooltip(
               bookingNumberRef,
               "wearable-card__booking-number",
               bookingNumber,
               isBookingNumberTruncated
            )}
         <div className="wearable-card__footer">
            <div className="wearable-card__location-wrapper">
               {renderWithTooltip(
                  locationRef,
                  "wearable-card__location",
                  location,
                  isLocationTruncated
               )}
            </div>

            {!awaitingVitals && (
               <div className="wearable-card__pill">
                  <span className="wearable-card__vital-group">
                     <span>{bpm}</span>
                     <Icon
                        name="heartRate"
                        size="12"
                        color={IconColor.WHITE}
                     />
                  </span>
                  <span className="wearable-card__vital-group">
                     <span>{oxygenLevel}</span>
                     <Icon
                        name="oxygenLevel"
                        size="12"
                        color={IconColor.WHITE}
                     />
                  </span>
               </div>
            )}
         </div>
      </div>
   )
}

export default WearableCardContent
